import elementClosest from "element-closest";
import React from "react";
import Sticky from "react-stickynode";
import { Editor as CoreEditor, Value } from "slate";
import HtmlSerialiser from "slate-html-serializer";
import PlainTextSerialiser from "slate-plain-serializer";
import { Editor } from "slate-react";
import { DataType, ITextEditorProps, ToolbarOptions } from ".";
import { isServer } from "../../../core/detection";
import { isFlagActive, FeatureFlag } from "../../helpers/featureFlags";
import { neverHere } from "@travellocal/utils/tsHelpers";
import { BlockSerialiserRule, InlineSerialiserRule, MarkSerialiserRule } from "./html";
import EditorPlugins from "./plugins";
import { SlateContextProvider } from "./slateContext";
import { Toolbar } from "./toolbar/toolbar";

import "./slate.scss"

type LocalState = Partial<{
  /**
   * Slate's document representation.
   */
  value: Value,
}>;

/**
 * Rich text editor implemented with Slate.js
 */
export class TextEditor extends React.PureComponent<ITextEditorProps, LocalState> {
  private static htmlSerialiser: HtmlSerialiser;
  public editorRef = React.createRef<Editor>();
  private toolbarRef = React.createRef<Toolbar>();

  public constructor(props: ITextEditorProps) {
    super(props);
    if (props.dataType === DataType.Html) {
      // Lazy initialisation
      TextEditor.htmlSerialiser = new HtmlSerialiser({
        rules: [
          BlockSerialiserRule,
          InlineSerialiserRule,
          MarkSerialiserRule,
        ],
      });
    }

    this.state = {
      value: TextEditor.stringToValue(props.dataType, props.initialValue || ""),
    };
  }

  /**
   * Deserialise a string into Slate's internal representation
   */
  private static stringToValue = (type: DataType, value: string): Value => {
    switch (type) {
      case DataType.PlainText:
        return PlainTextSerialiser.deserialize(value);
      case DataType.Html:
        return TextEditor.htmlSerialiser.deserialize(value);
      default:
        neverHere(type);
    }
  }

  /**
   * Serialise Slate's internal representation into either a plain text or HTML string
   */
  private static valueToString = (type: DataType, value: Value): string => {
    switch (type) {
      case DataType.PlainText:
        return PlainTextSerialiser.serialize(value);
      case DataType.Html:
        return TextEditor.htmlSerialiser.serialize(value);
      default:
        neverHere(type);
    }
  }

  /**
   * Handle change event from Slate.
   * N.B. This handler is fired a lot - not just when text content changes, but on focus, text selection, etc.
   */
  private onChange = ({ value }: { value: Value }): void => {
    const { dataType: mode, onChange } = this.props;

    if (onChange && value.document !== this.state.value.document) {
      onChange(TextEditor.valueToString(mode, value));
    }

    this.setState({ value }, () => {
      if (this.toolbarRef.current) {
        this.toolbarRef.current.forceUpdate()
      }
    });
  }

  public render() {

    if (isServer()) {
      // TODO - disabling server render for now as slate-instant-replace breaks
      return null;
    }

    elementClosest(window); // slate editor uses closest which isn't supported in IE

    const { toolbarBoundingElem, toolbarOptions, readOnly, onFocus, onBlur, placeholder } = this.props

    return (
      <SlateContextProvider>
        <>
          {!readOnly &&
            <Sticky innerZ={1} bottomBoundary={toolbarBoundingElem}>
              <Toolbar
                ref={this.toolbarRef}
                editorRef={this.editorRef.current as any as CoreEditor}
                toolbarOptions={toolbarOptions || [ToolbarOptions.Minimal]}
              />
            </Sticky>
          }

          <Editor
            className="slate-editor"
            ref={this.editorRef}
            value={this.state.value}
            placeholder={placeholder || "Write your message here..."}
            plugins={EditorPlugins}
            onChange={(value) => this.onChange(value)}
            autoFocus={true}
            spellCheck
            readOnly={readOnly}
            onFocus={(event, editor, next) => {
              if (onFocus) {
                onFocus();
              }
              next();
            }}
            onBlur={(event, editor, next) => {
              if (onBlur) {
                onBlur();
              }
              next();
            }}
            data-gramm={isFlagActive(FeatureFlag.SLATE_GRAMMARLY_2348)} // Enable for preview - possible bugs
          />
        </>
      </SlateContextProvider>
    );
  }
}
